import '@/styles/table.css';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, ChevronRight, EyeOff, Plus, Type } from '@/components/ui/icons';
import type { Location, Task, View, ViewColumn } from '@shared/types';
import { isCompleteType } from '@shared/constants';
import { getDb, useDb, useDbShallow } from '@/lib/store';
import { effectiveStatuses, fieldsForLocation, statusOf } from '@/lib/hierarchy';
import type { TaskGroup } from '@/lib/query';
import { updateTask, updateViewSettings } from '@/lib/actions';
import { formatDuration, formatMoney } from '@/lib/format';
import { useOpenTask } from '@/lib/nav';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem, MenuSeparator } from '@/components/ui/Menu';
import { openContextMenu } from '@/components/ui/overlays';
import { StatusIcon, StatusMenu, statusProgress } from '@/components/fields/Status';
import { TaskTypeIcon } from '@/components/fields/TaskCells';
import { ViewToolbar } from './ViewToolbar';
import { FieldsPanel } from './FieldsPanel';
import { Cell, resolveColumns, type ColumnDef } from './cells';
import { useViewQuery } from './useViewQuery';
import { TaskContextMenu } from '@/task/TaskContextMenu';
import { GroupLabel, createTaskInGroup, useSubtaskExpansion } from './table/shared';
import { TableBulkBar } from './table/BulkBar';

// Fixed geometry of the sticky leading columns (keep in sync with table.css).
const NUM_W = 44;
const CHECK_W = 32;
const NAME_W = 320;
const MIN_COL_W = 70;

const cssVar = (id: string) => `--tvc-${id.replace(/[^a-zA-Z0-9_-]/g, '-')}`;

type TableItem =
  | { kind: 'group'; key: string; group: TaskGroup; collapsed: boolean }
  | { kind: 'task'; key: string; task: Task; depth: number; row: number; hasKids: boolean; expanded: boolean }
  | { kind: 'add'; key: string; group: TaskGroup };

// ---------------------------------------------------------------------------
// Row

interface RowProps {
  task: Task;
  depth: number;
  row: number;
  columns: ColumnDef[];
  spaceId: string | undefined;
  selected: boolean;
  focusedCol: number | null;
  hasKids: boolean;
  expanded: boolean;
  renaming: boolean;
  onToggleExpand: (id: string) => void;
  onToggleSelect: (id: string, shift: boolean) => void;
  onFocusCell: (row: number, col: number) => void;
  onOpen: (id: string) => void;
  onRename: (id: string | null) => void;
}

const TableRow = memo(function TableRow(p: RowProps) {
  const { task } = p;
  const status = useDb((db) => statusOf(db, task));
  const statuses = useDbShallow((db) => effectiveStatuses(db, task.listId));
  const taskSpace = useDb((db) => db.lists[task.listId]?.spaceId);
  const closed = isCompleteType(status.type);
  const clickTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(clickTimer.current), []);

  const commitRename = (value: string) => {
    const v = value.trim();
    if (v && v !== task.name) updateTask(task.id, { name: v });
    p.onRename(null);
  };

  return (
    <div
      className={clsx('tv-row', { selected: p.selected, closed })}
      onContextMenu={(e) => openContextMenu(e, (close) => <TaskContextMenu task={task} close={close} />)}
    >
      <div className="tv-cell tv-num tv-sticky" style={{ left: 0 }}>
        {p.row + 1}
      </div>
      <div className="tv-cell tv-check tv-sticky" style={{ left: NUM_W }}>
        <input
          type="checkbox"
          className="checkbox"
          checked={p.selected}
          onChange={() => {}}
          onClick={(e) => {
            e.stopPropagation();
            p.onToggleSelect(task.id, e.shiftKey);
          }}
        />
      </div>
      <div
        className={clsx('tv-cell tv-name tv-sticky', p.focusedCol === 0 && 'focused')}
        style={{ left: NUM_W + CHECK_W }}
        data-cell={`${p.row}:0`}
        onMouseDown={() => p.onFocusCell(p.row, 0)}
        onClick={(e) => {
          if (p.renaming || e.detail > 1) return;
          window.clearTimeout(clickTimer.current);
          // delay so a double-click can turn into a rename instead of opening the task
          clickTimer.current = window.setTimeout(() => p.onOpen(task.id), 230);
        }}
        onDoubleClick={() => {
          window.clearTimeout(clickTimer.current);
          p.onRename(task.id);
        }}
      >
        {p.depth > 0 && <span className="tv-indent" style={{ width: p.depth * 20 }} />}
        <span className="tv-caret-slot">
          {p.hasKids && (
            <button
              className={clsx('tv-caret', { open: p.expanded })}
              onClick={(e) => {
                e.stopPropagation();
                p.onToggleExpand(task.id);
              }}
              onDoubleClick={(e) => e.stopPropagation()}
              title={p.expanded ? 'Collapse subtasks' : 'Expand subtasks'}
            >
              <ChevronRight size={13} />
            </button>
          )}
        </span>
        <Popover
          content={(close) => (
            <StatusMenu
              statuses={statuses}
              currentId={task.statusId}
              onPick={(s) => {
                updateTask(task.id, { statusId: s.id });
                close();
              }}
            />
          )}
        >
          <button className="tv-status-btn" title={status.name} onDoubleClick={(e) => e.stopPropagation()}>
            <StatusIcon type={status.type} color={status.color} progress={statusProgress(statuses, status)} />
          </button>
        </Popover>
        <TaskTypeIcon task={task} size={14} />
        {p.renaming ? (
          <input
            className="tv-rename"
            autoFocus
            defaultValue={task.name}
            onFocus={(e) => e.currentTarget.select()}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') commitRename(e.currentTarget.value);
              if (e.key === 'Escape') p.onRename(null);
            }}
            onBlur={(e) => commitRename(e.currentTarget.value)}
          />
        ) : (
          <span className="tv-title truncate">{task.name}</span>
        )}
      </div>
      {p.columns.map((c, i) => (
        <div
          key={c.id}
          className={clsx('tv-cell tv-data', p.focusedCol === i + 1 && 'focused')}
          data-cell={`${p.row}:${i + 1}`}
          onMouseDown={() => p.onFocusCell(p.row, i + 1)}
        >
          <Cell task={task} column={c} spaceId={p.spaceId ?? taskSpace ?? ''} />
        </div>
      ))}
      <div className="tv-cell tv-filler" />
    </div>
  );
});

// ---------------------------------------------------------------------------
// Inline "+ New task" row

function AddRow({
  group,
  location,
  active,
  onActivate,
  onDone,
}: {
  group: TaskGroup | undefined;
  location: Location;
  active: boolean;
  onActivate: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState('');
  const save = () => {
    if (!name.trim()) return;
    createTaskInGroup(location, group, name);
    setName('');
  };
  return (
    <div className={clsx('tv-row tv-add-row', { editing: active })} onClick={active ? undefined : onActivate}>
      <div className="tv-cell tv-num tv-sticky" style={{ left: 0 }}>
        <Plus size={13} />
      </div>
      <div className="tv-cell tv-add-cell tv-sticky" style={{ left: NUM_W }}>
        {active ? (
          <>
            {group?.status ? <StatusIcon type={group.status.type} color={group.status.color} /> : <StatusIcon type="open" color="var(--text-3)" />}
            <input
              className="tv-add-input"
              autoFocus
              value={name}
              placeholder="Task name — press Enter to save"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') save();
                if (e.key === 'Escape') onDone();
              }}
              onBlur={() => !name.trim() && setTimeout(onDone, 120)}
            />
            <button className="btn sm primary" onMouseDown={(e) => e.preventDefault()} onClick={save} disabled={!name.trim()}>
              Save ↵
            </button>
          </>
        ) : (
          <span className="tv-add-label">New task</span>
        )}
      </div>
      <div className="tv-cell tv-add-rest" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary footer

function summarize(c: ColumnDef, tasks: Task[]): React.ReactNode {
  if (c.id === 'timeEstimate') {
    const total = tasks.reduce((a, t) => a + (t.timeEstimate ?? 0), 0);
    return total ? (
      <>
        <span className="tv-sum-k">Sum</span> {formatDuration(total)}
      </>
    ) : null;
  }
  const f = c.field;
  if (!f || (f.type !== 'number' && f.type !== 'money')) return null;
  let total = 0;
  let any = false;
  for (const t of tasks) {
    const v = t.customFields[f.id];
    if (v === undefined || v === null || v === '') continue;
    const n = Number(v);
    if (!Number.isNaN(n)) {
      total += n;
      any = true;
    }
  }
  if (!any) return null;
  return (
    <>
      <span className="tv-sum-k">Sum</span> {f.type === 'money' ? formatMoney(total, f.config?.currency ?? 'USD', f.config?.precision ?? 2) : total.toLocaleString()}
    </>
  );
}

// ---------------------------------------------------------------------------

export function TableView({ view, location }: { view: View; location: Location }) {
  const [search, setSearch] = useState('');
  const s = view.settings;
  const q = useViewQuery(view, location, search);
  const customFields = useDb((db) => db.customFields);
  const available = useDb((db) => fieldsForLocation(db, location).map((f) => f.id).join(','));
  const columns = useMemo(() => resolveColumns(s.columns, customFields, new Set(available.split(','))), [s.columns, customFields, available]);
  const nameWidth = s.columns.find((c) => c.id === 'name')?.width ?? NAME_W;
  const spaceId = useDb((db) => (location.type === 'space' ? location.id : location.type === 'folder' ? db.folders[location.id]?.spaceId : location.type === 'list' ? db.lists[location.id]?.spaceId : undefined));
  const grouped = s.groupBy !== 'none';
  const collapsed = useMemo(() => new Set(s.collapsedGroups), [s.collapsedGroups]);
  const { isExpanded, toggle: toggleExpand } = useSubtaskExpansion(s.subtasks);

  const openTask = useOpenTask();
  const openRef = useRef(openTask);
  openRef.current = openTask;
  const onOpen = useCallback((id: string) => openRef.current(id), []);

  // flatten groups → rows ------------------------------------------------------
  const { items, taskRows } = useMemo(() => {
    const items: TableItem[] = [];
    const taskRows: Task[] = [];
    for (const g of q.groups) {
      const isCollapsed = grouped && collapsed.has(g.key);
      if (grouped) items.push({ kind: 'group', key: `g:${g.key}`, group: g, collapsed: isCollapsed });
      if (isCollapsed) continue;
      const walk = (t: Task, depth: number) => {
        const kids = q.children.get(t.id) ?? [];
        const exp = kids.length > 0 && isExpanded(t.id);
        items.push({ kind: 'task', key: `${g.key}:${t.id}`, task: t, depth, row: taskRows.length, hasKids: kids.length > 0, expanded: exp });
        taskRows.push(t);
        if (exp) for (const k of kids) walk(k, depth + 1);
      };
      for (const t of g.tasks) walk(t, 0);
      items.push({ kind: 'add', key: `a:${g.key}`, group: g });
    }
    return { items, taskRows };
  }, [q, grouped, collapsed, isExpanded]);

  const summaryTasks = useMemo(() => {
    const seen = new Map<string, Task>();
    for (const g of q.groups) for (const t of g.tasks) seen.set(t.id, t);
    return [...seen.values()];
  }, [q.groups]);

  // selection ----------------------------------------------------------------------
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const orderRef = useRef<string[]>([]);
  orderRef.current = taskRows.map((t) => t.id);
  const anchor = useRef<string | null>(null);
  const onToggleSelect = useCallback((id: string, shift: boolean) => {
    // read/advance the anchor outside the updater (StrictMode runs updaters twice)
    const from = shift ? anchor.current : null;
    const order = orderRef.current;
    anchor.current = id;
    setSelected((prev) => {
      const n = new Set(prev);
      const a = from ? order.indexOf(from) : -1;
      const b = order.indexOf(id);
      if (a >= 0 && b >= 0) for (const x of order.slice(Math.min(a, b), Math.max(a, b) + 1)) n.add(x);
      else if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }, []);
  const visibleIds = taskRows.map((t) => t.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = !allSelected && visibleIds.some((id) => selected.has(id));
  const selectedIds = [...selected].filter((id) => getDb().tasks[id]);

  // focused cell -----------------------------------------------------------------------
  const [focus, setFocus] = useState<{ row: number; col: number } | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [addingIn, setAddingIn] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const numCols = columns.length + 1;
  const onFocusCell = useCallback((row: number, col: number) => setFocus((f) => (f && f.row === row && f.col === col ? f : { row, col })), []);

  useEffect(() => {
    if (!focus) return;
    if (taskRows.length === 0) setFocus(null);
    else if (focus.row >= taskRows.length || focus.col >= numCols) setFocus({ row: Math.min(focus.row, taskRows.length - 1), col: Math.min(focus.col, numCols - 1) });
  }, [focus, taskRows.length, numCols]);

  useEffect(() => {
    if (!focus) return;
    gridRef.current?.querySelector<HTMLElement>(`[data-cell="${focus.row}:${focus.col}"]`)?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [focus]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    // ignore keys from portaled popovers (they bubble through the React tree) and text inputs
    if (!e.currentTarget.contains(target)) return;
    if (target.closest('textarea, [contenteditable="true"]')) return;
    if (target instanceof HTMLInputElement && target.type !== 'checkbox') return;
    if (!focus) {
      if (['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key) && taskRows.length) {
        e.preventDefault();
        setFocus({ row: 0, col: 0 });
      } else if (e.key === 'Escape') setSelected(new Set());
      return;
    }
    const move = (dr: number, dc: number) => {
      e.preventDefault();
      setFocus({
        row: Math.max(0, Math.min(taskRows.length - 1, focus.row + dr)),
        col: Math.max(0, Math.min(numCols - 1, focus.col + dc)),
      });
    };
    const task = taskRows[focus.row];
    switch (e.key) {
      case 'ArrowUp':
        return move(-1, 0);
      case 'ArrowDown':
        return move(1, 0);
      case 'ArrowLeft':
        return move(0, -1);
      case 'ArrowRight':
        return move(0, 1);
      case 'Tab':
        return move(0, e.shiftKey ? -1 : 1);
      case 'Home':
        // the Name column is sticky, so scrollIntoView can't bring the first columns back
        if (scrollRef.current) scrollRef.current.scrollLeft = 0;
        return move(0, -numCols);
      case 'End':
        return move(0, numCols);
      case 'Escape':
        // a cell picker is open: let it close first and keep the focus ring
        if (document.querySelector('.popover')) return;
        e.preventDefault();
        setFocus(null);
        return;
      case 'F2':
        e.preventDefault();
        if (task && focus.col === 0) setRenaming(task.id);
        return;
      case ' ':
        e.preventDefault();
        if (task) onToggleSelect(task.id, e.shiftKey);
        return;
      case 'Enter': {
        e.preventDefault();
        if (!task) return;
        if (focus.col === 0) {
          onOpen(task.id);
          return;
        }
        const cell = gridRef.current?.querySelector<HTMLElement>(`[data-cell="${focus.row}:${focus.col}"]`);
        cell?.querySelector<HTMLElement>('button, input[type="checkbox"], a')?.click();
        return;
      }
    }
  };

  // columns: sort / hide / move / resize ------------------------------------------------
  const sortOf = (id: string) => s.sort.find((x) => x.field === id)?.dir;
  const setSort = (field: string, dir: 'asc' | 'desc' | null) => updateViewSettings(view.id, { sort: dir ? [{ field, dir }] : [] });
  const hide = (id: string) => updateViewSettings(view.id, { columns: s.columns.map((c) => (c.id === id ? { ...c, hidden: true } : c)) });
  const moveColumn = (id: string, dir: -1 | 1) => {
    const vis = columns.map((c) => c.id);
    const i = vis.indexOf(id);
    const other = vis[i + dir];
    if (i < 0 || !other) return;
    const arr = [...s.columns];
    const a = arr.findIndex((c) => c.id === id);
    const b = arr.findIndex((c) => c.id === other);
    [arr[a], arr[b]] = [arr[b], arr[a]];
    updateViewSettings(view.id, { columns: arr });
  };
  const startResize = (id: string, e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const grid = gridRef.current;
    if (!grid) return;
    const startX = e.clientX;
    const startW = id === 'name' ? nameWidth : (columns.find((c) => c.id === id)?.width ?? 160);
    const min = id === 'name' ? 180 : MIN_COL_W;
    let w = startW;
    document.body.classList.add('tv-col-resizing');
    const onMove = (ev: PointerEvent) => {
      w = Math.max(min, Math.min(800, startW + ev.clientX - startX));
      grid.style.setProperty(cssVar(id), `${w}px`);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.classList.remove('tv-col-resizing');
      if (Math.round(w) === startW) return;
      const cur = getDb().views[view.id]?.settings.columns ?? s.columns;
      const next: ViewColumn[] = cur.some((c) => c.id === id) ? cur.map((c) => (c.id === id ? { ...c, width: Math.round(w) } : c)) : [{ id, width: Math.round(w) }, ...cur];
      updateViewSettings(view.id, { columns: next });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const gridStyle = useMemo(() => {
    const st: Record<string, string> = { '--tvc-name': `${nameWidth}px` };
    for (const c of columns) st[cssVar(c.id)] = `${c.width}px`;
    st['--tv-template'] = `${NUM_W}px ${CHECK_W}px var(--tvc-name) ${columns.map((c) => `var(${cssVar(c.id)})`).join(' ')} minmax(56px, 1fr)`;
    return st as React.CSSProperties;
  }, [columns, nameWidth]);

  const toggleGroup = (key: string) =>
    updateViewSettings(view.id, { collapsedGroups: collapsed.has(key) ? s.collapsedGroups.filter((k) => k !== key) : [...s.collapsedGroups, key] });

  const sortMenu = (id: string, close: () => void, extra?: React.ReactNode) => (
    <Menu style={{ width: 220 }}>
      <MenuItem icon={<ArrowUp size={15} />} label="Sort ascending" checked={sortOf(id) === 'asc'} onClick={() => (setSort(id, 'asc'), close())} />
      <MenuItem icon={<ArrowDown size={15} />} label="Sort descending" checked={sortOf(id) === 'desc'} onClick={() => (setSort(id, 'desc'), close())} />
      {sortOf(id) && <MenuItem label="Clear sort" onClick={() => (setSort(id, null), close())} />}
      {extra}
    </Menu>
  );
  const sortArrow = (id: string) => (sortOf(id) === 'asc' ? <ArrowUp size={11} /> : sortOf(id) === 'desc' ? <ArrowDown size={11} /> : null);

  return (
    <div className="tv">
      <ViewToolbar
        view={view}
        location={location}
        search={search}
        onSearch={setSearch}
        onAddTask={() => setAddingIn(q.groups[0]?.key ?? 'all')}
        features={{ group: true, subtasks: true, columns: true, sort: true }}
      />
      <div className="tv-scroll" ref={scrollRef} tabIndex={0} onKeyDown={onKeyDown}>
        <div className="tv-grid" ref={gridRef} style={gridStyle}>
          {/* header */}
          <div className="tv-row tv-head">
            <div className="tv-hcell tv-num tv-sticky" style={{ left: 0 }}>
              #
            </div>
            <div className="tv-hcell tv-check tv-sticky" style={{ left: NUM_W }}>
              <input
                type="checkbox"
                className="checkbox"
                title={allSelected ? 'Clear selection' : 'Select all'}
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={() => setSelected(allSelected ? new Set() : new Set(visibleIds))}
              />
            </div>
            <div className="tv-hcell tv-name tv-sticky" style={{ left: NUM_W + CHECK_W }}>
              <Popover content={(close) => sortMenu('name', close)}>
                <button className="tv-hbtn">
                  <Type size={13} />
                  <span className="truncate">Name</span>
                  {sortArrow('name')}
                </button>
              </Popover>
              <span className="tv-resize" onPointerDown={(e) => startResize('name', e)} />
            </div>
            {columns.map((c, i) => (
              <div key={c.id} className="tv-hcell">
                <Popover
                  content={(close) =>
                    sortMenu(
                      c.id,
                      close,
                      <>
                        <MenuSeparator />
                        {i > 0 && <MenuItem icon={<ArrowLeft size={15} />} label="Move left" onClick={() => (moveColumn(c.id, -1), close())} />}
                        {i < columns.length - 1 && <MenuItem icon={<ArrowRight size={15} />} label="Move right" onClick={() => (moveColumn(c.id, 1), close())} />}
                        <MenuItem icon={<EyeOff size={15} />} label="Hide column" onClick={() => (hide(c.id), close())} />
                      </>,
                    )
                  }
                >
                  <button className="tv-hbtn">
                    <span className="tv-hicon">{c.icon}</span>
                    <span className="truncate">{c.label}</span>
                    {sortArrow(c.id)}
                  </button>
                </Popover>
                <span className="tv-resize" onPointerDown={(e) => startResize(c.id, e)} />
              </div>
            ))}
            <div className="tv-hcell tv-addcol-cell">
              <Popover placement="bottom-end" content={() => <FieldsPanel location={location} settings={s} onChange={(c) => updateViewSettings(view.id, c)} />}>
                <button className="tv-addcol" title="Add column">
                  <Plus size={14} />
                </button>
              </Popover>
            </div>
          </div>

          {/* body */}
          {summaryTasks.length === 0 && (
            <div className="tv-empty">
              <span className="tv-empty-title">No tasks found</span>
              <span className="faint">Try changing filters, or add a task.</span>
            </div>
          )}
          {items.map((it) => {
            if (it.kind === 'group') {
              const g = it.group;
              return (
                <div key={it.key} className="tv-group-row">
                  <div className="tv-group-inner">
                    <button className={clsx('tv-group-caret', { open: !it.collapsed })} onClick={() => toggleGroup(g.key)}>
                      <ChevronRight size={14} />
                    </button>
                    <GroupLabel group={g} />
                    <span className="tv-group-count">{g.tasks.length}</span>
                    <button
                      className="tv-group-add"
                      onClick={() => {
                        if (it.collapsed) toggleGroup(g.key);
                        setAddingIn(g.key);
                      }}
                    >
                      <Plus size={13} /> Add Task
                    </button>
                  </div>
                </div>
              );
            }
            if (it.kind === 'add') {
              return (
                <AddRow
                  key={it.key}
                  group={it.group}
                  location={location}
                  active={addingIn === it.group.key}
                  onActivate={() => setAddingIn(it.group.key)}
                  onDone={() => setAddingIn((cur) => (cur === it.group.key ? null : cur))}
                />
              );
            }
            return (
              <TableRow
                key={it.key}
                task={it.task}
                depth={it.depth}
                row={it.row}
                columns={columns}
                spaceId={spaceId}
                selected={selected.has(it.task.id)}
                focusedCol={focus && focus.row === it.row ? focus.col : null}
                hasKids={it.hasKids}
                expanded={it.expanded}
                renaming={renaming === it.task.id}
                onToggleExpand={toggleExpand}
                onToggleSelect={onToggleSelect}
                onFocusCell={onFocusCell}
                onOpen={onOpen}
                onRename={setRenaming}
              />
            );
          })}
          {q.groups.length === 0 && (
            <AddRow group={undefined} location={location} active={addingIn !== null} onActivate={() => setAddingIn('all')} onDone={() => setAddingIn(null)} />
          )}

          {/* summary */}
          <div className="tv-row tv-summary">
            <div className="tv-cell tv-num tv-sticky" style={{ left: 0 }} />
            <div className="tv-cell tv-sum-label tv-sticky" style={{ left: NUM_W }}>
              {summaryTasks.length} task{summaryTasks.length === 1 ? '' : 's'}
            </div>
            {columns.map((c) => (
              <div key={c.id} className="tv-cell tv-sum">
                {summarize(c, summaryTasks)}
              </div>
            ))}
            <div className="tv-cell tv-filler" />
          </div>
        </div>
      </div>
      {selectedIds.length > 0 && <TableBulkBar ids={selectedIds} clear={() => setSelected(new Set())} />}
    </div>
  );
}
